import React, { useCallback } from "react";
import {
  Box,
  Typography,
  Switch,
  Tooltip,
  Chip,
  Divider,
  FormControlLabel,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockIcon from "@mui/icons-material/Lock";
import { isSystemLockedField, systemConstraints } from "../doctor-console/config";
import {
  sortByOrder,
  reorderByIndex,
  useDragReorder,
  dragRowSx,
  dragListContainerSx,
} from "./reorderUtils";
import {
  MANAGER_COLORS,
  fieldHeaderRowSx,
  helperCaptionSx,
  typeChipSx,
} from "./managerTheme.js";

// Field types that never carry a user-entered value, so "Required" can't apply to them.
const NOT_REQUIRABLE_TYPES = new Set(["dentalChart", "drawingCanvas", "s3DocumentPicker"]);

const isFieldRequirable = (field) =>
  !field.readOnly &&
  !field.uiOnly &&
  !field.computed &&
  !NOT_REQUIRABLE_TYPES.has(field.type);

// Fields whose components already enforce "required when a trigger field is set"
// (see FollowUp.js / Referral.js validation) even when this manager's Req toggle is
// off. Mirrored here as a read-only lock so the manager UI doesn't misrepresent them
// as optional — this does not change any existing validation logic.
const CONDITIONAL_REQUIRED_RULES = [
  {
    fieldSetId: "followUpFields",
    fieldId: "service_type",
    triggerFieldId: "duration",
    tooltip: "Description is required if follow-up duration is selected.",
  },
  {
    fieldSetId: "referralFields",
    fieldId: "doctor_details",
    triggerFieldId: "service_category",
    tooltip: "Doctor selection is required if a department is selected.",
  },
];

const getConditionalRequiredRule = (fieldSetId, fieldId) =>
  CONDITIONAL_REQUIRED_RULES.find(
    (rule) => rule.fieldSetId === fieldSetId && rule.fieldId === fieldId,
  );

const SectionFieldsEditor = ({ fieldSetId, fieldSet, onChange }) => {
  const fields = fieldSet?.fields ?? [];
  const tableColumns = fieldSet?.tableColumns ?? [];
  const pattern = fieldSet?.pattern;

  const handleFieldReorder = useCallback(
    (dragIndex, dropIndex) => {
      if (!fieldSetId || !fieldSet) return;
      onChange(fieldSetId, {
        ...fieldSet,
        fields: reorderByIndex(fields, dragIndex, dropIndex),
      });
    },
    [fields, fieldSet, fieldSetId, onChange],
  );

  const { handleDragStart, handleDragOver, handleDrop } = useDragReorder(handleFieldReorder);

  if (!fieldSetId || !fieldSet) return null;

  const sortedFields = sortByOrder(fields);
  const fieldConstraints = systemConstraints.fields?.[fieldSetId] ?? {};

  const updateField = (id, patch) => {
    let nextFields = fields.map((f) => (f.id === id ? { ...f, ...patch } : f));
    // A conditionally-required field can't be hidden while its trigger field is
    // shown, so keep the two in sync when the trigger is turned on.
    if (patch.enabled === true) {
      const dependentRule = CONDITIONAL_REQUIRED_RULES.find(
        (rule) => rule.fieldSetId === fieldSetId && rule.triggerFieldId === id,
      );
      if (dependentRule) {
        nextFields = nextFields.map((f) =>
          f.id === dependentRule.fieldId ? { ...f, enabled: true } : f,
        );
      }
    }

    // When a field is hidden, also hide its matching collapsed-preview column —
    // a column can't preview a field that isn't shown. Only when no other
    // still-shown field shares the same key (some fields bind to one column key).
    let nextColumns = tableColumns;
    if (patch.enabled === false) {
      const columnKey = nextFields.find((f) => f.id === id)?.key;
      const otherShownSharesKey =
        !!columnKey &&
        nextFields.some(
          (f) => f.id !== id && f.key === columnKey && f.enabled !== false,
        );
      if (columnKey && !otherShownSharesKey) {
        nextColumns = tableColumns.map((c) =>
          c.key === columnKey ? { ...c, enabled: false } : c,
        );
      }
    }

    onChange(fieldSetId, {
      ...fieldSet,
      fields: nextFields,
      tableColumns: nextColumns,
    });
  };

  const toggleColumn = (key, checked) => {
    onChange(fieldSetId, {
      ...fieldSet,
      fields,
      tableColumns: tableColumns.map((c) => (c.key === key ? { ...c, enabled: checked } : c)),
    });
  };

  // A preview column whose field key is known but has no still-shown field is
  // "orphaned" — the field it previews is hidden, so the column is hidden too.
  const shownFieldKeys = new Set(
    fields.filter((f) => f.enabled !== false).map((f) => f.key).filter(Boolean),
  );
  const allFieldKeys = new Set(fields.map((f) => f.key).filter(Boolean));
  const isColumnFieldHidden = (key) => allFieldKeys.has(key) && !shownFieldKeys.has(key);

  return (
    <Box>
      <Typography sx={helperCaptionSx}>Drag rows to reorder fields.</Typography>

      <Box sx={dragListContainerSx}>
        <Box sx={fieldHeaderRowSx}>
          <Box sx={{ width: 24 }} />
          <Typography sx={{ width: 52 }}>Show</Typography>
          <Typography sx={{ width: 52 }}>Req</Typography>
          <Box sx={{ width: 20 }} />
          <Typography sx={{ flex: 1, minWidth: 120 }}>Label</Typography>
          <Typography sx={{ width: 100, textAlign: "right" }}>Type</Typography>
        </Box>

        {sortedFields.map((field, index) => {
          // Branded/Generic (medicationRow) are UI-only checkboxes with no real config
          // meaning in this manager, so they're hidden from the field list here.
          // Row is skipped (not filtered out of sortedFields) so drag-reorder indices
          // for the remaining fields stay aligned with reorderByIndex's sorted array.
          if (field.id === "drugType_branded" || field.id === "drugType_generic") {
            return null;
          }

          // Follow-up Date is computed from duration and is force-hidden
          // (display: none) in FollowUp.js regardless of this flag, so its
          // toggle here has no real effect. Hidden from the manager list.
          if (fieldSetId === "followUpFields" && field.id === "start_date") {
            return null;
          }

          const locked = isSystemLockedField(fieldSetId, field.id);
          const constraint = fieldConstraints[field.id];
          const requirable = isFieldRequirable(field);
          const conditionalRule = getConditionalRequiredRule(fieldSetId, field.id);
          const triggerEnabled =
            !!conditionalRule &&
            fields.find((f) => f.id === conditionalRule.triggerFieldId)?.enabled !== false;
          const showLockedByTrigger = conditionalRule && triggerEnabled;

          return (
            <Box
              key={field.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              sx={{ ...dragRowSx, flexWrap: "wrap" }}
            >
              <DragIndicatorIcon fontSize="small" sx={{ color: MANAGER_COLORS.primary, flexShrink: 0 }} />

              <Tooltip
                title={
                  locked
                    ? constraint?.reason ?? "Locked"
                    : showLockedByTrigger
                      ? conditionalRule.tooltip
                      : "Show/hide this field"
                }
              >
                <span>
                  <Switch
                    size="small"
                    checked={showLockedByTrigger ? true : field.enabled !== false}
                    disabled={locked || showLockedByTrigger}
                    onChange={(e) => updateField(field.id, { enabled: e.target.checked })}
                  />
                </span>
              </Tooltip>

              <Tooltip
                title={
                  locked
                    ? constraint?.reason ?? "Locked"
                    : !requirable
                      ? "This field has no user-entered value, so it can't be marked required."
                      : conditionalRule
                        ? conditionalRule.tooltip
                        : "Required field"
                }
              >
                <span>
                  <FormControlLabel
                    sx={{ ml: 0, mr: 0, width: 52 }}
                    control={
                      <Switch
                        size="small"
                        checked={locked ? true : field.validation?.required === true}
                        disabled={locked || !requirable}
                        onChange={(e) =>
                          updateField(field.id, {
                            validation: { ...field.validation, required: e.target.checked },
                          })
                        }
                      />
                    }
                    label=""
                  />
                </span>
              </Tooltip>

              {locked ? (
                <Tooltip title={constraint?.reason ?? "Locked (required for FHIR mapping)"}>
                  <LockIcon fontSize="small" sx={{ color: MANAGER_COLORS.primary, opacity: 0.5, width: 20 }} />
                </Tooltip>
              ) : (
                <Box sx={{ width: 20 }} />
              )}

              <Typography variant="body2" sx={{ flex: 1, minWidth: 120 }}>
                {field.label ?? field.key}
              </Typography>

              <Chip size="small" label={field.type} variant="outlined" sx={typeChipSx} />
            </Box>
          );
        })}
      </Box>

      {tableColumns.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: MANAGER_COLORS.primaryDark }}>
            Collapsed table preview columns
          </Typography>
          <Box sx={dragListContainerSx}>
            {sortByOrder(tableColumns).map((column) => {
              const fieldHidden = isColumnFieldHidden(column.key);
              return (
                <Box key={column.key} sx={{ ...dragRowSx, cursor: "default", "&:hover": { bgcolor: MANAGER_COLORS.card } }}>
                  <Tooltip
                    title={
                      fieldHidden
                        ? "The matching field is hidden, so this column is hidden too. Show the field to enable it."
                        : "Show/hide this column in the collapsed table preview"
                    }
                  >
                    <span>
                      <Switch
                        size="small"
                        checked={column.enabled !== false && !fieldHidden}
                        disabled={fieldHidden}
                        onChange={(e) => toggleColumn(column.key, e.target.checked)}
                      />
                    </span>
                  </Tooltip>
                  <Typography variant="body2">{column.label}</Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {pattern && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Pattern: <code style={{ color: MANAGER_COLORS.primary }}>{pattern}</code>
        </Typography>
      )}
    </Box>
  );
};

export default SectionFieldsEditor;
